import React, { ReactNode, useMemo } from 'react';
import { ConfigProvider, theme } from 'antd';
import { useTheme } from '../../../providers/ThemeProvider';
import { Mode } from '@cloudscape-design/global-styles';

interface FormThemeProviderProps {
  children: ReactNode;
}

/**
 * FormThemeProvider bridges Cloudscape theme settings to Ant Design components
 * This ensures form components respect the global light/dark mode setting
 */
export const FormThemeProvider: React.FC<FormThemeProviderProps> = ({ children }) => {
  const { mode } = useTheme();

  // Memoize theme object to prevent unnecessary recalculations
  const antdTheme = useMemo(() => ({
    algorithm: mode === Mode.Dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      // Primary colors - keeping consistent with Cloudscape design
      colorPrimary: mode === Mode.Dark ? '#0073bb' : '#0073bb',
      colorSuccess: mode === Mode.Dark ? '#037f0c' : '#037f0c',
      colorWarning: mode === Mode.Dark ? '#b7811b' : '#b7811b',
      colorError: mode === Mode.Dark ? '#d13212' : '#d13212',
      
      // Background colors
      colorBgContainer: mode === Mode.Dark ? '#1e1e1e' : '#ffffff',
      colorBgElevated: mode === Mode.Dark ? '#2a2a2a' : '#ffffff',
      colorBgLayout: mode === Mode.Dark ? '#0d1117' : '#f5f5f5',
      
      // Text colors
      colorText: mode === Mode.Dark ? '#ffffff' : '#000000',
      colorTextSecondary: mode === Mode.Dark ? '#d0d7de' : '#656565',
      colorTextTertiary: mode === Mode.Dark ? '#8b949e' : '#999999',
      colorTextDisabled: mode === Mode.Dark ? '#484f58' : '#cccccc',
      
      // Border colors
      colorBorder: mode === Mode.Dark ? '#30363d' : '#d1d7e0',
      colorBorderSecondary: mode === Mode.Dark ? '#21262d' : '#e9ecef',
      
      // Form specific tokens
      controlHeight: 32,
      fontSize: 14,
      borderRadius: 4,
      
      // Focus and hover states
      colorPrimaryHover: mode === Mode.Dark ? '#1890ff' : '#40a9ff',
      colorPrimaryActive: mode === Mode.Dark ? '#096dd9' : '#096dd9',
    },
    components: {
      // Form component specific styling
      Form: {
        labelFontSize: 14,
        labelColor: mode === Mode.Dark ? '#ffffff' : '#000000',
        labelRequiredMarkColor: mode === Mode.Dark ? '#ff4d4f' : '#ff4d4f',
      },
      Input: {
        colorBgContainer: mode === Mode.Dark ? '#0d1117' : '#ffffff',
        colorText: mode === Mode.Dark ? '#ffffff' : '#000000',
        colorTextPlaceholder: mode === Mode.Dark ? '#8b949e' : '#999999',
        colorBorder: mode === Mode.Dark ? '#30363d' : '#d1d7e0',
        borderRadius: 4,
        controlHeight: 32,
      },
      Select: {
        colorBgContainer: mode === Mode.Dark ? '#0d1117' : '#ffffff',
        colorText: mode === Mode.Dark ? '#ffffff' : '#000000',
        colorTextPlaceholder: mode === Mode.Dark ? '#8b949e' : '#999999',
        colorBorder: mode === Mode.Dark ? '#30363d' : '#d1d7e0',
        borderRadius: 4,
        controlHeight: 32,
      },
      Checkbox: {
        colorText: mode === Mode.Dark ? '#ffffff' : '#000000',
        colorBorder: mode === Mode.Dark ? '#30363d' : '#d1d7e0',
      },
      Radio: {
        colorText: mode === Mode.Dark ? '#ffffff' : '#000000',
        colorBorder: mode === Mode.Dark ? '#30363d' : '#d1d7e0',
      },
      Table: {
        colorBgContainer: mode === Mode.Dark ? '#0d1117' : '#ffffff',
        colorText: mode === Mode.Dark ? '#ffffff' : '#000000',
        colorBorderSecondary: mode === Mode.Dark ? '#21262d' : '#e9ecef',
        headerBg: mode === Mode.Dark ? '#161b22' : '#f8f9fa',
        headerColor: mode === Mode.Dark ? '#ffffff' : '#000000',
      },
      TreeSelect: {
        colorBgContainer: mode === Mode.Dark ? '#0d1117' : '#ffffff',
        colorText: mode === Mode.Dark ? '#ffffff' : '#000000',
        colorTextPlaceholder: mode === Mode.Dark ? '#8b949e' : '#999999',
        colorBorder: mode === Mode.Dark ? '#30363d' : '#d1d7e0',
        colorBgElevated: mode === Mode.Dark ? '#161b22' : '#ffffff',
        borderRadius: 4,
        controlHeight: 32,
      }
    }
  }), [mode]);

  return (
    <ConfigProvider theme={antdTheme}>
      {children}
    </ConfigProvider>
  );
};
